import {CurrentUser} from "./App.context";
import {
  UserInputAction,
  AvatarAction,
  SystemAction,
  ChoiceUserInputAction,
  UserInputActionInput,
  AvatarActionInput,
  SystemActionInput,
  ChoiceUserInputActionInput,
  User,
  Editor,
  Student,
  Trainer,
  Manager,
  Admin,
  Scalars,
  Scenario as GQLScenario,
  ChallengeScenarioActivationMethod,
  Project,
  Challenge,
  Maybe,
} from "./schema";
export type Action = UserInputAction | AvatarAction | SystemAction | ChoiceUserInputAction;
export type ActionWithMedia = (SystemAction | AvatarAction) & {
  media: string;
  mediaType: string;
};
export type ActionInput = UserInputActionInput | AvatarActionInput | SystemActionInput | ChoiceUserInputActionInput;
export type Scenario = Omit<GQLScenario, "actions" | "id" | "name" | "isMain"> & {
  actions: Action[],
  id: Scalars["ID"],
  index: number,
  nextScenarioIndex: number,
  name: Scalars["String"],
  isMain: Scalars["Boolean"],
  nextScenario: Maybe<Scalars["String"]>;
  activationMethod?: Maybe<ChallengeScenarioActivationMethod>;
}

export type SessionResultFeedback = {
  text: string;
  audioUrl: string | null;
  feedbackStatus: "NOT_STARTED" | "IN_PROGRESS" | "READY" | "ERROR" | "DISABLED";
};

export function getChallengeActionTextRepr(action: Action) {
  switch (action.__typename) {
    case "UserInputAction":
      return action.referenceText;

    case "SystemAction":
      return action.text;

    case "AvatarAction":
      return action.text;

    case "ChoiceUserInputAction":
      return undefined;

    default:
      console.error(`Unsupported action type "${action.__typename}" in getChallengeActionTextRepr`);
      return undefined;
  }
}

export type Nullable<T> = T | null | undefined;

export type DeepNullable<T> = T extends Function
  ? T
  : T extends Array<infer U>
    ? _DeepNullableArray<U>
    : T extends object
      ? _DeepNullableObject<T>
      : T | null;

interface _DeepNullableArray<T> extends Array<DeepNullable<T>> {}

type _DeepNullableObject<T> = {[P in keyof T]: DeepNullable<T[P]> | null};

export function isUserEditor(user: User & TypedItem<string>): user is Editor {
  return user?.__typename === "Editor";
}

export function isUserStudent(user: User & TypedItem<string>): user is Student {
  return user?.__typename === "Student";
}

export function isUserTrainer(user: User & TypedItem<string>): user is Trainer {
  return user?.__typename === "Trainer";
}

export function isUserManager(user: User & TypedItem<string>): user is Manager {
  return user?.__typename === "Manager";
}

export function isUserAdmin(user: (User | CurrentUser) & TypedItem<string>): user is Admin {
  return user?.__typename === "Admin";
}

export function isSharedId(challengeId: string): boolean {
  return !/^\d+$/.test(challengeId);
}

type SharedItem = {
  id: Scalars["ID"];
  accessToken: Scalars["String"];
};

export function getSharedId<T extends SharedItem>(item: T) {
  return `${item.id}_${item.accessToken}`;
}

export function isActionWithMedia(action: Nullable<Action>): action is ActionWithMedia {
  return isNonUserAction(action) && !!action.media && !!action.mediaType;
}

export function isNonUserAction(action: Nullable<Action>): action is SystemAction | AvatarAction {
  return isSystemAction(action) || isAvatarAction(action);
}

export function isAvatarAction(action: Nullable<Action>): action is AvatarAction {
  return action?.__typename === "AvatarAction";
}

export function isSystemAction(action: Nullable<Action>): action is SystemAction {
  return action?.__typename === "SystemAction";
}

export function isUserInputAction(action: Nullable<Action>): action is UserInputAction {
  return action?.__typename === "UserInputAction";
}

export function isChoiceUserInputAction(action: Nullable<Action>): action is ChoiceUserInputAction {
  return action?.__typename === "ChoiceUserInputAction";
}

export function isMultipleChoiceUserInputAction(action: Nullable<Action>): action is ChoiceUserInputAction {
  return action?.__typename === "ChoiceUserInputAction" && !!action?.isMultipleChoice;
}

export function isChallenge(item: TypedItem<string>): item is Challenge {
  return item?.__typename === "Challenge"
}

export function isProject(item: TypedItem<string>): item is Project {
  return item?.__typename === "Project"
}

export type TypedItem<K> = {
  __typename?: K;
};

export type WithoutTypename<T extends TypedItem<string>> = Omit<T, "__typename">;
